import { useEffect, useRef } from 'react';
import { animate, useMotionValue, useReducedMotion, useTransform } from 'framer-motion';
import { SPRING } from './motionTokens';

// Drives an indicator by its two edges on separate springs: the edge moving toward the
// target leads on a fast spring, the other trails, so the shape stretches then settles.
// When `moveKey` is given, only a change of it (e.g. the selected index) animates; other
// target changes (fonts loading, a resize) snap, so layout shifts never read as motion.
export const useStretchEdges = (targetLeft: number, targetRight: number, moveKey?: unknown) => {
  const left = useMotionValue(targetLeft);
  const right = useMotionValue(targetRight);
  const first = useRef(true);
  const lastKey = useRef(moveKey);
  // The key usually changes a render before the new edges are measured, so the move
  // stays pending until the targets actually change.
  const pendingMove = useRef(false);
  const lastTargets = useRef([targetLeft, targetRight]);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (lastKey.current !== moveKey) pendingMove.current = true;
    lastKey.current = moveKey;
    const targetsChanged = lastTargets.current[0] !== targetLeft || lastTargets.current[1] !== targetRight;
    lastTargets.current = [targetLeft, targetRight];
    if (!first.current && !targetsChanged) return undefined;
    if (first.current || reducedMotion || (moveKey !== undefined && !pendingMove.current)) {
      first.current = false;
      left.jump(targetLeft);
      right.jump(targetRight);
      return undefined;
    }
    pendingMove.current = false;
    const movingRight = targetLeft > left.get();
    const leftAnim = animate(left, targetLeft, movingRight ? SPRING.trail : SPRING.lead);
    const rightAnim = animate(right, targetRight, movingRight ? SPRING.lead : SPRING.trail);
    return () => {
      leftAnim.stop();
      rightAnim.stop();
    };
  }, [left, right, targetLeft, targetRight, moveKey, reducedMotion]);

  const width = useTransform(() => Math.max(0, right.get() - left.get()));
  return { left, right, width };
};
